import React, { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget for the merchant portal. Renders the challenge and
 * calls `onToken` with the response token (or null on expiry/error). If
 * REACT_APP_TURNSTILE_SITE_KEY is not set it renders nothing, so the form keeps
 * working until the key is configured. The token is verified server-side by the
 * monolith (sent as the X-Turnstile-Token header on register).
 */

declare global {
	interface Window {
		turnstile?: {
			render: (el: HTMLElement, opts: Record<string, unknown>) => string;
			remove: (id: string) => void;
			reset: (id?: string) => void;
		};
	}
}

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export const turnstileConfigured = !!SITE_KEY;

/** Hidden honeypot input — real users never fill it; naive bots do. */
export function Honeypot({
	value,
	onChange,
}: {
	value: string;
	onChange: (v: string) => void;
}) {
	return (
		<input
			type="text"
			name="company"
			tabIndex={-1}
			autoComplete="off"
			value={value}
			onChange={(e) => onChange(e.target.value)}
			aria-hidden="true"
			style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
		/>
	);
}

export function Turnstile({ onToken }: { onToken: (token: string | null) => void }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<string | null>(null);
	const cbRef = useRef(onToken);
	cbRef.current = onToken;

	useEffect(() => {
		if (!SITE_KEY) return;
		let cancelled = false;

		const renderWidget = () => {
			if (cancelled || widgetIdRef.current || !containerRef.current || !window.turnstile) return;
			widgetIdRef.current = window.turnstile.render(containerRef.current, {
				sitekey: SITE_KEY,
				callback: (token: string) => cbRef.current(token),
				"expired-callback": () => cbRef.current(null),
				"error-callback": () => cbRef.current(null),
			});
		};

		if (window.turnstile) {
			renderWidget();
		} else {
			let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
			if (!script) {
				script = document.createElement("script");
				script.src = SCRIPT_SRC;
				script.async = true;
				script.defer = true;
				document.head.appendChild(script);
			}
			script.addEventListener("load", renderWidget);
			const poll = setInterval(() => {
				if (window.turnstile) {
					clearInterval(poll);
					renderWidget();
				}
			}, 200);
			setTimeout(() => clearInterval(poll), 10000);
		}

		return () => {
			cancelled = true;
			if (widgetIdRef.current && window.turnstile) {
				try {
					window.turnstile.remove(widgetIdRef.current);
				} catch {
					/* already removed */
				}
				widgetIdRef.current = null;
			}
		};
	}, []);

	if (!SITE_KEY) return null;
	return <div ref={containerRef} style={{ margin: "8px 0" }} />;
}
